import React, { useState, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router';
import PropTypes from 'prop-types';
import queryString from 'query-string';

import { useDebounce } from 'use-debounce';

import useIntersection from '@/utils/hooks/useIntersection';
import useWindowIsMobile from '@/utils/hooks/useWindowIsMobile';

import parseObjectToQuery from '@/utils/functions/parseObjectToQuery';
import CustomSelect from '@/views/components/CustomSelect/CustomSelect';

import styles from '@/utils/pagination/Pagination.module.scss';

/* Instrukcja użycia:
Wiemy, że dany blok będzie wymagał modułu paginacji, otaczamy go tym komponentem:
<Pagination>
    <ExampleList />
</Pagination>
w ten sposób przekazany zostaje pierwszy parametr - children
W nadrzędnym komponencie musimy tylko zdefiniować stan dla obecnego numeru strony:
const [currentPage, setCurrentPage] = setState(1);
Api zawssze podaje nam też ilość stron, a zatem to przypisujemy jako trzeci parametr

onGoToPage to referencja do funkcji, która jest odpowiedzialna za zmianę strony
(na przykład użytkownik w module paginacja kliknął na 3 stronę, czyli w komponencie powyżej musimy zrobić przekierowanie na odpowiednią stronę, coś w stylu:
navigate('examplelist/3')
    )
*/

/* PRZERABIAMY - tak, żeby potrzebne były tylko children i pagesNum 
dane brane z adresu i tak samo tu jest przeładowywanie w historii */

const Pagination = ({
	children,
	pagesNum = null,
	pageString = 'page',
	sortingOptions = undefined,
	sortingValue = undefined,
	sortingOnChange = undefined,
	alignRight = false,
}) => {
	const { dict } = useSelector((state) => state.app);
	const [ currentPage, setCurrentPage ] = useState(1);

	const location = useLocation();
	const navigate = useNavigate();

	const [ pagesArr, setPagesArr ] = useState([]);
	const [ inputPageNum, setInputPageNum ] = useState(0);
	const [ debouncedPageNum ] = useDebounce(inputPageNum, 500);
	const topAnchor = useRef();

	const { isIntersecting } = useIntersection( topAnchor );

	const isMobile = useWindowIsMobile(991);

	useEffect(() => {
		const db = parseInt(debouncedPageNum);
		if (db !== currentPage && db > 0 && db <= pagesNum) {
			gotoPage(db);
		}
		// eslint-disable-next-line
    }, [debouncedPageNum]);

	const gotoPage = (pageNum) => {
		if (!isIntersecting) {
			topAnchor.current.scrollIntoView({
				behavior: 'smooth',
				block: 'start',
			});
		}
		const qu = queryString.parse(location.search);
		navigate(`${location.pathname}?${parseObjectToQuery({ ...qu, [pageString]: pageNum })}`);
	};

	useEffect(() => {
		const qu = queryString.parse(location.search);
		const page = parseInt(qu[pageString]);
		if ( page && page > 0 ) {
			setCurrentPage(page);
		} else {
			setCurrentPage(1);
		}
	}, [ location.search ]);

	useEffect(() => {
		if (pagesNum && currentPage > pagesNum) {
			const urlParamsObject = Object.fromEntries(new URLSearchParams(location.search));
			urlParamsObject.page = pagesNum;
			navigate(`${location.pathname}?${new URLSearchParams(urlParamsObject).toString()}`, { replace: true });
		}
	}, [ pagesNum ]);

	const prevButton = (
		<button  
			className={ `${(currentPage > 1) ? styles["pagination__btn"] : styles["pagination__btn--disabled"]}
					${styles["pagination__btn--prev"]}` }
			onClick={ () => gotoPage(currentPage - 1) }
		>
			{ dict.pagination.previous }
		</button>
	);
	const nextButton = (
		<button
			className={ `${(currentPage < pagesNum) ? styles["pagination__btn"] : styles["pagination__btn--disabled"]}
					${styles["pagination__btn--next"]}` }
			onClick={ () => gotoPage(currentPage + 1) }
		>
			{ dict.pagination.next }
		</button>
	);

	const allPagesNumButton = (pageNum) => (
		<li
			className={ styles["pagination-bottom__item"] }
		>
			<button
				className={ styles["pagination__page-btn"] }
				onClick={ () => {
					if (pageNum) {
						gotoPage(pageNum);
					} else {
						gotoPage(1);
					}
				} }
			>
				{pageNum ? pageNum : 1}
			</button>
		</li>
	);

	useEffect(() => {
		setPagesArr([ ...Array(pagesNum).keys() ].filter((item, index) => {
			let surroundingPageNum = 2;

			if ((currentPage === 1) || (pagesNum >= 5 && currentPage === pagesNum)) {
				surroundingPageNum = 4;
			} else if ((currentPage === 2) || (pagesNum === 4 && currentPage === 4) || (pagesNum > 4 && currentPage === pagesNum - 1)) {
				surroundingPageNum = 3;
			}

			if (index >= (currentPage - surroundingPageNum - 1) && index <= (currentPage + surroundingPageNum - 1)) {
				return item + 1;
			}
			return null;
		}));
		if (currentPage) {
			setInputPageNum(currentPage);
		}
		// eslint-disable-next-line
    }, [currentPage, isMobile, pagesNum]);

	return (
		<>
			<div className={ `${styles["pagination-top"]} ${alignRight ? styles["pagination-top--right"] : ''}` }>
				{ sortingOptions && <div className={ styles["pagination-top__sort"] }>
					<div className={ styles["pagination-top__sort__text"] }>{ dict.pagination.sort }</div>
					<CustomSelect
						options={ sortingOptions } 
						value={ sortingValue }
						onChange={ sortingOnChange }
						placeholder=""
					/> 
				</div>}
				<div className={ styles["pagination-top__pages"] }>
					{(currentPage > 1) && prevButton}
					{(pagesNum > 1) ? (
						<input
							className={ styles["pagination__input"] }
							type='number'
							min='1'
							value={ inputPageNum }
							onChange={ ev => setInputPageNum(ev.target.value) }
							max={ pagesNum }
						/>
					) : <div className={ styles["pagination__page-btn--selected"] }>{ currentPage }</div>}
					<div className={ styles["pagination__page-btn--selected"] }>{ dict.pagination.of }</div>
					{ allPagesNumButton(pagesNum) }
					{ (currentPage < pagesNum) && nextButton }
				</div>
			</div>

			{ children }

			<div className={ `${styles["pagination-bottom"]} ${alignRight ? styles["pagination-bottom--right"] : ''}` }>
				{isMobile ? <>
					{(currentPage > 1) && prevButton}
					{(pagesNum > 1) ? (
						<input
							className={ styles["pagination__input"] }
							type='number'
							min='1'
							value={ inputPageNum }
							onChange={ ev => setInputPageNum(ev.target.value) }
							max={ pagesNum }
						/>
					) : <div className={ styles["pagination__page-btn--selected"] }>{ currentPage }</div>}
					<div className={ styles["pagination__page-btn--selected"] }>{ dict.pagination.of }</div>
					{ allPagesNumButton(pagesNum) }
					{ (currentPage < pagesNum) && nextButton }
				</> : <>
					{prevButton}
					{ ( pagesNum > 5 && currentPage >= 4 ) && ( allPagesNumButton(1) )}
					
					{ ( pagesNum > 5 && currentPage >= 5 ) && (
						<li className={ styles["pagination-bottom__item--disabled"] }>
							<div className={ styles["pagination__page-btn--selected"] }>...</div>
						</li>
					)}

					<ul className={ `${styles["pagination-bottom__list"]}` }>
						{ pagesNum ? (
							<>
								{ pagesArr.map((page, index) => (
									<li
										className={ (page + 1 === currentPage) ? styles["pagination-bottom__item--disabled"] : styles["pagination-bottom__item"] }
										key={ "page" + index.toString() }
									>
										<button 
											className={ (page + 1 === currentPage) ? styles["pagination__page-btn--selected"] : styles["pagination__page-btn"] } 
											disabled={ page + 1 === currentPage }
											onClick={ () => gotoPage(page + 1) }
										>
											{page + 1}
										</button>
									</li>
								),
								)}
							</>
						) : (
							<>
								<li
									className={ styles["pagination-bottom__item--disabled"] }
									key={ "page1" }
								>
									<button 
										className={ styles["pagination__page-btn--selected"] } 
										disabled={ true }
										onClick={ () => gotoPage(1) }
									>
										1
									</button>
								</li>
							</>
						)}
						<li className={ styles["pagination-bottom__item--disabled"] }>
							<div className={ styles["pagination__page-btn--selected"] }>
								{ dict.pagination.of }
							</div>
						</li>
						{allPagesNumButton(pagesNum)}
					</ul>
					{nextButton}
				</>}
			</div>
		</>
	);
};

Pagination.propTypes = {
	children: PropTypes.oneOfType([ PropTypes.string, PropTypes.element ]),
	pagesNum: PropTypes.number,
	pageString: PropTypes.string,
};

export default Pagination;